import { useEffect, useState, useCallback, useId } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Search, Building2, Pencil, Ban, Trash2, RefreshCcw, X, Megaphone, Receipt } from "lucide-react";
import useAccessibleOverlay from "../../hooks/useAccessibleOverlay";

const STATUSES = ["", "active", "suspended", "deleted"];
const PLANS = ["free", "basic", "pro", "enterprise", "custom"];
const PAID_PLANS = ["basic", "pro", "enterprise", "custom"];
const ADDON_KEYS = ["monthly_fleet_points", "permanent_fleet_points"];
const PAYMENT_METHODS = ["bank_transfer", "gcash", "maya", "cash", "card_manual", "comped", "other"];

export default function AdminBusinesses() {
  const [items, setItems] = useState([]);
  const [placements, setPlacements] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [recordingPayment, setRecordingPayment] = useState(null);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (status) params.set("status", status);
    const [{ data }, { data: sp }] = await Promise.all([
      api.get(`/admin/businesses?${params}`),
      api.get(`/admin/sponsored-placements`),
    ]);
    setItems(data.items || []);
    setPlacements(sp.items || []);
  }, [q, status]);

  useEffect(() => { load(); }, [load]);

  // Only one placement is ever "current" per business for this simple admin
  // view: the most recently created active one whose date window covers
  // right now. Everything else (cancelled, expired, historical) is just
  // campaign history, not shown in this compact table.
  const activePlacementByCompany = {};
  const now = new Date();
  for (const p of placements) {
    if (p.status !== "active") continue;
    if (new Date(p.starts_at) > now || new Date(p.ends_at) < now) continue;
    const cur = activePlacementByCompany[p.company_id];
    if (!cur || new Date(p.created_at) > new Date(cur.created_at)) {
      activePlacementByCompany[p.company_id] = p;
    }
  }

  const sponsorFor30Days = async (companyId) => {
    try {
      const startsAt = new Date();
      const endsAt = new Date(startsAt.getTime() + 30 * 24 * 60 * 60 * 1000);
      await api.post(`/admin/companies/${companyId}/sponsored-placement`, {
        starts_at: startsAt.toISOString(), ends_at: endsAt.toISOString(),
        billing_state: "manual_paid",
      });
      toast.success("Sponsored placement activated for 30 days.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not activate."); }
  };

  const cancelSponsorship = async (companyId, placementId) => {
    try {
      await api.post(`/admin/companies/${companyId}/sponsored-placement/${placementId}/cancel`);
      toast.success("Sponsored placement cancelled.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not cancel."); }
  };

  const onSave = async (patch) => {
    try {
      await api.put(`/admin/businesses/${editing.id}`, patch);
      toast.success("Business updated.");
      setEditing(null);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error((detail && (detail.message || (typeof detail === "string" ? detail : null))) || "Update failed.");
    }
  };

  const onRecordPayment = async (payload) => {
    const { kind, ...rest } = payload;
    const path = kind === "addon"
      ? `/admin/businesses/${recordingPayment.id}/billing/grant-addon`
      : `/admin/businesses/${recordingPayment.id}/billing/manual-activation`;
    try {
      await api.post(path, rest);
      toast.success(kind === "addon" ? "Add-on granted." : "Plan activated.");
      setRecordingPayment(null);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error((detail && (detail.message || (typeof detail === "string" ? detail : null))) || "Could not record payment.");
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/businesses/${confirm.id}`);
      toast.success("Business soft-deleted.");
      setConfirm(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed."); }
  };

  return (
    <div data-testid="admin-businesses-page">
      <h1 className="font-display text-2xl font-semibold flex items-center gap-2">
        <Building2 className="w-5 h-5" /> Businesses
      </h1>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <input data-testid="admin-businesses-search" value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search by name..."
                 className="pl-7 pr-3 py-1.5 text-sm rounded-lg border border-slate-300 focus:border-slate-500 outline-none" />
        </div>
        <select data-testid="admin-businesses-status-filter" value={status} onChange={(e) => setStatus(e.target.value)}
                className="px-2 py-1.5 text-sm rounded-lg border border-slate-300">
          {STATUSES.map((s) => <option key={s} value={s}>{s || "Any status"}</option>)}
        </select>
        <button onClick={load} className="px-3 py-1.5 text-sm rounded-lg border border-slate-300 hover:bg-slate-50 inline-flex items-center gap-1">
          <RefreshCcw className="w-3.5 h-3.5" /> Refresh
        </button>
        <span className="text-xs text-slate-500 ml-auto">{items.length} businesses</span>
      </div>
      <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="text-left p-2">Name</th>
              <th className="text-left p-2">Type</th>
              <th className="text-left p-2">City</th>
              <th className="text-left p-2">Plan</th>
              <th className="text-left p-2">Status</th>
              <th className="text-left p-2">Sub</th>
              <th className="text-left p-2">Sponsored</th>
              <th className="text-right p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((b) => (
              <tr key={b.id} data-testid={`biz-row-${b.id}`} className="border-t border-slate-100">
                <td className="p-2 font-medium">{b.name}</td>
                <td className="p-2 text-slate-600">{b.business_type}</td>
                <td className="p-2 text-slate-600">{b.city}</td>
                <td className="p-2"><span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 uppercase">{b.plan_type || "-"}</span></td>
                <td className="p-2 text-slate-600">{b.status}</td>
                <td className="p-2 text-slate-600">{b.subscription_status}</td>
                <td className="p-2">
                  {activePlacementByCompany[b.id] ? (
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-900 border border-amber-300 uppercase font-semibold inline-flex items-center gap-1">
                        <Megaphone className="w-3 h-3" /> Until {new Date(activePlacementByCompany[b.id].ends_at).toLocaleDateString()}
                      </span>
                      <button
                        onClick={() => cancelSponsorship(b.id, activePlacementByCompany[b.id].id)}
                        data-testid={`biz-sponsor-cancel-${b.id}`}
                        className="text-xs px-1.5 py-0.5 rounded border border-slate-300 text-slate-600 hover:bg-slate-50"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : b.status === "active" && b.show_in_public_directory !== false ? (
                    <button
                      onClick={() => sponsorFor30Days(b.id)}
                      data-testid={`biz-sponsor-activate-${b.id}`}
                      className="text-xs px-2 py-1 rounded border border-slate-300 text-slate-600 hover:bg-slate-50 inline-flex items-center gap-1"
                    >
                      <Megaphone className="w-3 h-3" /> Sponsor (30d)
                    </button>
                  ) : (
                    <span className="text-[10px] text-slate-400">Not eligible</span>
                  )}
                </td>
                <td className="p-2 text-right">
                  {!b.is_demo && (
                    <button data-testid={`biz-record-payment-${b.id}`} onClick={() => setRecordingPayment(b)}
                            className="mr-2 text-xs px-2 py-1 rounded border border-emerald-300 text-emerald-800 hover:bg-emerald-50 inline-flex items-center gap-1">
                      <Receipt className="w-3 h-3" /> Record payment
                    </button>
                  )}
                  <button data-testid={`biz-edit-${b.id}`} onClick={() => setEditing(b)}
                          className="text-xs px-2 py-1 rounded border border-slate-300 hover:bg-slate-50 inline-flex items-center gap-1">
                    <Pencil className="w-3 h-3" /> Edit
                  </button>
                  <button data-testid={`biz-delete-${b.id}`} onClick={() => setConfirm(b)}
                          className="ml-2 text-xs px-2 py-1 rounded border border-rose-300 text-rose-700 hover:bg-rose-50 inline-flex items-center gap-1">
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-slate-500">No businesses match.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Edit modal */}
      {editing && <EditModal item={editing} onClose={() => setEditing(null)} onSave={onSave} />}

      {recordingPayment && (
        <RecordPaymentModal item={recordingPayment} onClose={() => setRecordingPayment(null)}
                            onSubmit={onRecordPayment} />
      )}

      {/* Confirm-destructive modal */}
      {confirm && (
        <Modal title={`Delete "${confirm.name}"?`} onClose={() => setConfirm(null)} testId="confirm-delete-biz">
          <p className="text-sm text-slate-700">This soft-deletes the company (status = <code>deleted</code>). Data is kept. You can change it back from the edit form.</p>
          <div className="mt-4 flex justify-end gap-2">
            <button onClick={() => setConfirm(null)} className="px-3 py-1.5 text-sm rounded border border-slate-300">Cancel</button>
            <button onClick={doDelete} data-testid="confirm-delete-biz-yes"
                    className="px-3 py-1.5 text-sm rounded bg-rose-600 text-white">Yes, soft-delete</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function EditModal({ item, onClose, onSave }) {
  const [form, setForm] = useState({
    name: item.name || "",
    status: item.status || "active",
    plan_type: item.plan_type || "basic",
    notes: item.notes || "",
    reason: "",
  });
  const planChanges = form.plan_type !== (item.plan_type || "");
  const canSave = !planChanges || form.reason.trim().length >= 5;
  const save = () => {
    const { reason, ...rest } = form;
    onSave(planChanges ? { ...rest, reason: reason.trim() } : rest);
  };
  return (
    <Modal title={`Edit ${item.name}`} onClose={onClose} testId="biz-edit-modal">
      <div className="space-y-3 text-sm">
        <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
        <FieldSelect label="Status" value={form.status} options={["active", "suspended", "deleted"]}
                       onChange={(v) => setForm({ ...form, status: v })} />
        <FieldSelect label="Plan" value={form.plan_type} options={PLANS}
                       onChange={(v) => setForm({ ...form, plan_type: v })} />
        {planChanges && (
          <FieldTextarea label="Reason for plan change (required, audited)" value={form.reason}
                         testId="biz-edit-plan-reason"
                         onChange={(v) => setForm({ ...form, reason: v })} />
        )}
        <FieldTextarea label="Notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} />
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <button onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-slate-300">Cancel</button>
        <button data-testid="biz-edit-save" onClick={save} disabled={!canSave}
                className="px-3 py-1.5 text-sm rounded bg-[#1B4332] text-white disabled:opacity-60">Save</button>
      </div>
    </Modal>
  );
}

function RecordPaymentModal({ item, onClose, onSubmit }) {
  const [form, setForm] = useState({
    kind: "plan",
    plan: PAID_PLANS.includes(item.plan_type) ? item.plan_type : "basic",
    period_days: 30,
    addon_key: ADDON_KEYS[0],
    payment_method: "bank_transfer",
    payment_reference: "",
    reason: "",
  });
  const canSubmit = form.reason.trim().length >= 5 && Number(form.period_days) >= 1;
  const submit = () => {
    const common = {
      kind: form.kind,
      payment_method: form.payment_method,
      payment_reference: form.payment_reference.trim() || null,
      reason: form.reason.trim(),
    };
    onSubmit(form.kind === "addon"
      ? { ...common, addon_key: form.addon_key }
      : { ...common, plan: form.plan, period_days: Number(form.period_days) });
  };
  return (
    <Modal title={`Record payment: ${item.name}`} onClose={onClose} testId="biz-record-payment-modal">
      <p className="text-xs text-slate-600 mb-3">
        Use this only after money was actually received outside online checkout (or for an approved comp).
        Every action is audited with your account, the reason and the before/after billing state.
      </p>
      <div className="space-y-3 text-sm">
        <FieldSelect label="What was paid for" value={form.kind} options={["plan", "addon"]}
                     testId="record-payment-kind" onChange={(v) => setForm({ ...form, kind: v })} />
        {form.kind === "plan" ? (
          <>
            <FieldSelect label="Plan" value={form.plan} options={PAID_PLANS}
                         testId="record-payment-plan" onChange={(v) => setForm({ ...form, plan: v })} />
            <Field label="Paid period (days)" type="number" value={form.period_days}
                   testId="record-payment-days" onChange={(v) => setForm({ ...form, period_days: v })} />
          </>
        ) : (
          <FieldSelect label="Add-on" value={form.addon_key} options={ADDON_KEYS}
                       testId="record-payment-addon" onChange={(v) => setForm({ ...form, addon_key: v })} />
        )}
        <FieldSelect label="Payment method" value={form.payment_method} options={PAYMENT_METHODS}
                     testId="record-payment-method" onChange={(v) => setForm({ ...form, payment_method: v })} />
        <Field label="Payment reference (optional)" value={form.payment_reference}
               testId="record-payment-reference" onChange={(v) => setForm({ ...form, payment_reference: v })} />
        <FieldTextarea label="Reason (required, audited)" value={form.reason}
                       testId="record-payment-reason" onChange={(v) => setForm({ ...form, reason: v })} />
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <button onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-slate-300">Cancel</button>
        <button data-testid="record-payment-submit" onClick={submit} disabled={!canSubmit}
                className="px-3 py-1.5 text-sm rounded bg-[#1B4332] text-white disabled:opacity-60">Record</button>
      </div>
    </Modal>
  );
}

export function Modal({ title, children, onClose, testId }) {
  const titleId = useId();
  const { panelRef } = useAccessibleOverlay(true, onClose);
  return (
    <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4" data-testid={testId} aria-hidden="false">
      <div ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}
           className="bg-white rounded-2xl border border-slate-200 shadow-xl w-full max-w-lg max-h-[calc(100vh-2rem)] overflow-y-auto p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 id={titleId} className="font-display text-lg font-semibold">{title}</h2>
          <button type="button" onClick={onClose} aria-label="Close dialog" className="min-w-11 min-h-11 grid place-items-center text-slate-500 hover:text-slate-700"><X className="w-4 h-4" aria-hidden="true" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Field({ label, value, onChange, type = "text", testId }) {
  return (
    <label className="block">
      <span className="text-xs text-slate-600 font-medium">{label}</span>
      <input type={type} data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)}
              className="mt-1 w-full px-3 py-1.5 rounded border border-slate-300 focus:border-slate-500 outline-none" />
    </label>
  );
}

export function FieldSelect({ label, value, options, onChange, testId }) {
  return (
    <label className="block">
      <span className="text-xs text-slate-600 font-medium">{label}</span>
      <select data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)}
               className="mt-1 w-full px-3 py-1.5 rounded border border-slate-300 focus:border-slate-500 outline-none">
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}

export function FieldTextarea({ label, value, onChange, testId }) {
  return (
    <label className="block">
      <span className="text-xs text-slate-600 font-medium">{label}</span>
      <textarea data-testid={testId} value={value || ""} onChange={(e) => onChange(e.target.value)}
                  rows={3}
                  className="mt-1 w-full px-3 py-1.5 rounded border border-slate-300 focus:border-slate-500 outline-none" />
    </label>
  );
}
